// 必要なモジュールや型定義をインポート
import UserFrame from "../organisms/UserFrame"; // ユーザー用共通フレーム
import { 
    Box, Button, CardContent, Container, Drawer, Grid, IconButton, 
    List, ListItem, ListItemButton, ListItemText, Stack, Typography, useMediaQuery 
} from '@mui/material'; // Material-UIコンポーネント
import { useEffect, useState } from "react"; // Reactのフック
import ChevronRightIcon from '@mui/icons-material/ChevronRight'; // 右向き矢印アイコン
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'; // 左向き矢印アイコン
import { GuestMaterialType } from "../types/guestMaterial";

// Propsの型定義
type EntryMaterialTemplateProps = {
    title: string; // ページタイトル
    data: GuestMaterialType[]; // 表示する資料データのリスト
    no: string | null; // noを追加
};

/** Entry 資料テンプレート */
const EntryMaterialTemplate: React.FC<EntryMaterialTemplateProps> = ({ title, data, no }) => {
    // noを使用して何か処理を行う
    console.log(no);

    const matches: boolean = useMediaQuery("(min-width:640px)"); // 画面幅が640px以上かどうかを判定
    const [open, setOpen] = useState(matches); // サイドメニューの開閉状態

    return (
        <UserFrame title={title} isEntry>
          <Box height={'100%'}>
            {/* 上部の「目次を表示/隠す」ボタン */}
            <Box sx={{display: 'flex', justifyContent: 'flex-end', minHeight: '2.5rem', position: 'fixed', zIndex: '10'}} bgcolor={'lightgray'} width={'100%'}>
                <Box padding={1}>
                    <Button
                        startIcon={open ? <ChevronRightIcon /> : <ChevronLeftIcon />} // ボタンのアイコン
                        onClick={() => setOpen(!open)} // ボタン押下時の処理
                        size="small"
                        color="secondary"
                    >
                        {`${open ? "目次を隠す" : "目次を表示"}`} {/* ボタンのラベル */}
                    </Button>
                </Box>
            </Box>
            <Box height={'3rem'}/> {/* ボタン分の高さ調整 */}
            
            {/* PC画面レイアウト */}
            {matches ?
            <Grid container sx={{position: 'fixed'}}>
                {/* メインコンテンツ */}
                <Grid item xs={open ? 7 : 12} maxHeight={`${document.documentElement.clientHeight * 0.9}px`} sx={{overflowY: 'scroll'}}>
                    {data.map((e, idx) => (
                        <Box key={`c_${idx}`} marginTop={(idx > 0 && !e.unindex) ? '1rem' : undefined}>
                            {/* 見出しがある場合の区切り */}
                            {(idx > 0 && !e.unindex) && <Box height='1.5rem' bgcolor='#e6e6b0' marginBottom='1rem'/>}
                            <div id={`id_${idx}`} /> {/* スクロール用アンカー */}
                            <Box marginLeft='1rem'>
                                <Typography variant={e.unindex ? undefined : "h6"}>
                                    {`${e.unindex ? "" : "■ "}${e.title}`} {/* タイトル表示 */}
                                </Typography>
                            </Box>
                            {/* コンテンツの画像やテキスト */}
                            <Stack sx={{padding: 1}} spacing={1} direction={open ? 'column' : 'row'}>
                                {e.imgPath && <Box minWidth={"640px"}>
                                    <picture>
                                        <img src={e.imgPath} width={e.width ?? undefined} alt=""/>
                                    </picture>
                                </Box>
                                }
                                {e.text &&
                                    <CardContent>
                                        {e.text.map((t, i) => (
                                            <Typography key={`t_${idx}_${i}`}>{t}</Typography>
                                        ))}
                                        {e.obj} {/* 任意のオブジェクト */}
                                    </CardContent>
                                }
                            </Stack>
                        </Box>
                    ))}
                    <Box height={'10rem'}/> {/* スクロール調整用の余白 */}
                </Grid>
                {/* サイドメニュー */}
                {open && <Grid item xs={5}>
                    <Container maxWidth='md'>
                        <List sx={{ width: '100%', bgcolor: 'background.paper', position: 'fixed' }}>
                            {data.map((e, i) => {
                                const labelId = `checkbox-list-label-${i}`;
                                if (!e.unindex) { // インデックス化されている項目のみ表示
                                    return (
                                        <ListItem key={`list_${i}`} disablePadding sx={{backgroundColor: '#f8f8f8'}}>
                                            <ListItemButton dense onClick={() => {
                                                const target = document.getElementById(`id_${i}`);
                                                if (target) {
                                                    target.scrollIntoView({ behavior: "smooth" });
                                                }
                                            }}>
                                                <ListItemText id={labelId} primary={`${e.header ? e.header+" " : ""}${e.title}`} />
                                            </ListItemButton>
                                        </ListItem>
                                    );
                                }
                                return null; // ←これを追加
                            })}
                        </List>
                    </Container>
                </Grid>}
            </Grid>
            :
            /* スマートフォン画面レイアウト */
            <Box height={`auto`} width={'100%'} sx={{overflowY:'scroll', overflowX:'hidden'}}>
                {data.map((e, idx) => (
                    <Box key={`c_${idx}`} marginTop={(idx > 0 && !e.unindex) ? '1rem' : undefined} width={'100%'} position={'relative'}>
                        <div id={`id_${idx}`} style={{position: "absolute", top: '-100px'}}/> {/* スクロール用アンカー */}
                        {(idx > 0 && !e.unindex) && <Box height='1.5rem' bgcolor='#e6e6b0' marginBottom='1rem'/>}
                        <Box marginLeft='1rem' width={'95%'}>
                            <Typography variant={e.unindex ? undefined : "subtitle1"}>
                                {`${e.unindex ? "" : "■ "}${e.title}`}
                            </Typography>
                        </Box>
                        <Stack sx={{padding: 1}} spacing={1} direction={'column'} width={'100%'}>
                            {e.imgPath && <Box width={'100%'}>
                                <picture>
                                    <img src={e.imgPath} width={`${window.innerWidth * 0.9}px`} alt=""/>
                                </picture>
                            </Box>}
                            {e.text &&
                                <CardContent>
                                    {e.text.map((t, i) => (
                                        <Typography key={`t_${idx}_${i}`}>{t}</Typography>
                                    ))}
                                    {e.obj}
                                </CardContent>
                            }
                        </Stack>
                    </Box>
                ))}
            </Box>}
            
            {/* スマホ用Drawerメニュー */}
            {!matches && <Drawer open={open} variant="persistent" anchor="right">
                <Box padding={1} width={"300px"}>
                    <Button
                        startIcon={open ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                        onClick={() => setOpen(!open)}
                        size="small"
                        color="secondary"
                    >
                        {`${open ? "目次を隠す" : "目次を表示"}`}
                    </Button>
                </Box>
                <Container>
                    <List sx={{ width: '100%', bgcolor: 'background.paper'}}>
                        {data.map((e, i) => {
                            const labelId = `checkbox-list-label2-${i}`;
                            if (!e.unindex) {
                                return (
                                <ListItem key={`list2_${i}`} disablePadding sx={{backgroundColor: '#f8f8f8'}}>
                                    <ListItemButton dense onClick={() => {
                                        const target = document.getElementById(`id_${i}`);
                                        if (target) {
                                            target.scrollIntoView({ behavior: "smooth" });
                                        }
                                    }}>
                                        <ListItemText id={labelId} primary={`${e.header ? e.header+" " : ""}${e.title}`} />
                                    </ListItemButton>
                                </ListItem>
                                );
                            }
                            return null; // ←これを追加
                        })}
                    </List>
                </Container>
            </Drawer>}
          </Box>
        </UserFrame>
    );
}

export default EntryMaterialTemplate;
